import { lazy, Suspense, useEffect, useState, type ComponentType } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Sidebar, TopBar, NAV_ITEMS } from '@strata/ui';
import { DemoBanner } from './components/DemoBanner.js';

// Per-screen code-splitting (PERFORMANCE-BUDGETS §5): each route is its own chunk,
// so the cockpit ships without the other 8 screens' bundles.
const Cockpit = lazy(() => import('./routes/Cockpit.js'));
const Balance = lazy(() => import('./routes/Balance.js'));
const Trends = lazy(() => import('./routes/Trends.js'));
const Savings = lazy(() => import('./routes/Savings.js'));
const Iso50001 = lazy(() => import('./routes/Iso50001.js'));
const Assets = lazy(() => import('./routes/Assets.js'));
const Capture = lazy(() => import('./routes/Capture.js'));
const Reports = lazy(() => import('./routes/Reports.js'));
const Alerts = lazy(() => import('./routes/Alerts.js'));

interface ScreenRoute {
  path: string;
  Component: ComponentType;
}

const SCREENS: ScreenRoute[] = [
  { path: '/', Component: Cockpit },
  { path: '/balance', Component: Balance },
  { path: '/trends', Component: Trends },
  { path: '/savings', Component: Savings },
  { path: '/iso50001', Component: Iso50001 },
  { path: '/assets', Component: Assets },
  { path: '/capture', Component: Capture },
  { path: '/reports', Component: Reports },
  { path: '/alerts', Component: Alerts },
];

function titleForPath(pathname: string): string {
  const item =
    NAV_ITEMS.find((n) => (n.end ? n.to === pathname : pathname.startsWith(n.to) && n.to !== '/')) ??
    NAV_ITEMS[0];
  return item?.label ?? 'Strata';
}

export function App() {
  const { pathname } = useLocation();
  const [navOpen, setNavOpen] = useState(false);

  // Close the mobile drawer on navigation so a deep link or back-button never leaves it open.
  useEffect(() => {
    setNavOpen(false);
  }, [pathname]);

  return (
    <div className="min-h-full bg-app-bg text-text">
      <Sidebar open={navOpen} onClose={() => setNavOpen(false)} />
      {/* Sidebar is docked from `lg`; below that it's an overlay drawer, so the content reclaims the full width. */}
      <div className="lg:pl-sidebar">
        <DemoBanner />
        <TopBar
          title={titleForPath(pathname)}
          userName="South Deep User"
          onMenuClick={() => setNavOpen(true)}
        />
        <main className="p-4 sm:p-6">
          <Suspense fallback={<ScreenSkeleton />}>
            <Routes>
              {SCREENS.map(({ path, Component }) => (
                <Route key={path} path={path} element={<Component />} />
              ))}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </main>
      </div>
    </div>
  );
}

function ScreenSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
      {Array.from({ length: 4 }, (_, i) => (
        <div key={i} className="h-32 animate-pulse rounded-2xl border border-border bg-surface" />
      ))}
    </div>
  );
}
